/**
 * Обзор бренда — первый экран кабинета и образец того, как экраны здесь
 * устроены.
 *
 * Порядок обязателен (app-kit.md §1): сначала сводка — одно число, одна дельта,
 * одно основание, — потом подробности. И ни одного числа, кроме пришедших из
 * контракта: до загрузки на экране стоит «Не измерено», а не ноль и не прочерк.
 *
 * Экран специально проходит через все шесть состояний каркаса, потому что в
 * измерительном продукте они и есть обычная жизнь: замера ещё не было, замер
 * идёт, сетка заполнилась не целиком, за период пусто, квота кончилась, роль
 * только смотрит.
 *
 * **Честность разреза по движкам.** Список строится от справочника движков, а
 * не от того, что вернулось в снимке: движок, который не ответил, обязан быть
 * виден строкой «Не измерено», иначе средняя по экрану молча улучшается ровно
 * тогда, когда источник сломался.
 *
 * Прогресс тянется **опросом**: потока событий в продукте нет, интервал задаёт
 * сервер заголовком `Retry-After`, а в терминальном ответе его нет — это и есть
 * сигнал остановиться (docs/API.md §8).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { requestWith } from '../api/client';
import { ExportFailure, orderAndDownload } from '../api/exports';
import { ApiError } from '../api/problem';
import { useAccess } from '../session';
import { useScope } from '../scope';
import { Empty, ErrorNotice, Skeleton } from '../ui/state';
import {
  Measuring,
  NeverMeasured,
  pickBrand,
  PartialRun,
  QuotaExhausted,
  scanBlockedFallback,
} from '../ui/screen-states';
import { useRead } from '../ui/query';
import { ScanCadencePicker } from '../ui/cadence';
import { LiveAnswer, useAnswers } from '../ui/LiveAnswer';
import { MEMORY_QUESTIONS_PATH, MemoryBlock } from '../ui/Memory';
import { byEngineOrder, engineTitle, engineTitles } from '../ui/controls';
import { snapshotBasis } from '../ui/basis';
import { brandSeries, rivalSeriesOf } from '../ui/series';
import { Bars, IndexGauge, Pill, Stamp, Trend, type BarItem } from '../ui/viz';
import type { Series } from '../ui/viz';
import {
  NOT_MEASURED,
  countOf,
  coverageOf,
  dateProse,
  deltaPoints,
  deltaPosition,
  indexDelta,
  int,
  percent,
  position,
  sentiment as sentimentFmt,
  sentimentDelta,
  timeOfDay,
} from '@shared/src/format';
import type { components } from '../api/schema';

type BrandOverview = components['schemas']['BrandOverview'];
type OrganizationQuotas = components['schemas']['OrganizationQuotas'];
type EngineWithHealth = components['schemas']['EngineWithHealth'];
type AttributeCloud = components['schemas']['AttributeCloud'];
type DailySnapshot = components['schemas']['DailySnapshot'];
type CompetitorSnapshot = components['schemas']['CompetitorSnapshot'];
type ReportBuildFormat = components['schemas']['ReportBuildFormat'];
type Report = components['schemas']['Report'];
type QuestionVisibilityPage = components['schemas']['QuestionVisibilityPage'];

/** Окно динамики на обзоре: месяц назад от показанной точки. */
const WINDOW_DAYS = 30;

/** Соперников на графике — не больше трёх: больше линий не различает никто. */
const RIVAL_LINES = 3;

/**
 * Что предлагается кнопкой на обзоре.
 *
 * Два формата, за которыми приходят с готовым числом на руках: PDF — чтобы
 * приложить к письму, XLSX — чтобы считать дальше. Страница и данные остаются
 * в центре отчётов: их берут осознанно, а не «дай файл».
 */
const EXPORTS: ReportBuildFormat[] = ['pdf', 'xlsx'];

/** Названия форматов выгрузки — те же, что в центре отчётов. */
const FORMAT: Record<ReportBuildFormat, string> = {
  pdf: 'PDF',
  xlsx: 'XLSX',
  html: 'Страница',
  json: 'Данные (JSON)',
};

/** Дата на столько суток раньше данной. Календарём занимается `Date`, не мы. */
function backFrom(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() - days);
  return at.toISOString().slice(0, 10);
}

/** Интервал опроса по умолчанию — три секунды, как в контракте. */
const POLL_FALLBACK = 3;

/** Дальше трёх неудач подряд интервал не растим: 3 → 6 → 12 секунд. */
const POLL_MAX = 12;

type Load =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      overview: BrandOverview;
      /**
       * Очередной опрос не дошёл, но числа на экране настоящие — просто снятые
       * в `since`. Это **не** состояние ошибки: экран остаётся на месте, сверху
       * появляется неблокирующая полоса. Стирать показанные числа из-за одной
       * не дошедшей попытки нельзя — продукт сам обещает человеку обратное.
       */
      stale?: { since: number };
    }
  | { kind: 'failed'; error: unknown };

export function Overview() {
  const scope = useScope();
  const access = useAccess();
  const brandId = scope.brandId;

  const [quotas, setQuotas] = useState<OrganizationQuotas | null>(null);

  // Квоты нужны, чтобы отличить «замеров не было» от «замеры остановлены».
  // Наблюдатель их видит, клиент агентства — нет, поэтому спрашиваем только
  // того, кто дотянется: 403 на экране обзора никому не нужен.
  useEffect(() => {
    if (!access.reaches('GET', '/organizations/current/quotas')) return;
    const abort = new AbortController();
    requestWith<OrganizationQuotas>('/organizations/current/quotas', {
      signal: abort.signal,
    })
      .then(({ body }) => setQuotas(body))
      .catch(() => undefined);
    return () => abort.abort();
  }, [access]);

  const head = (
    <div className="screen-head">
      <h1>{scope.brand ? scope.brand.name : 'Обзор бренда'}</h1>
    </div>
  );

  // Область ещё грузится — форма будущего содержимого, без бегущего блика.
  if (scope.state.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={2} />
      </>
    );
  }

  if (scope.state.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice error={scope.state.error} />
      </>
    );
  }

  // Ни одного живого бренда в организации — самый частый первый экран. Архив
  // здесь не считается: обзор показывает числа, а по архивному их не будет.
  if (scope.liveBrands.length === 0) {
    return (
      <>
        {head}
        {/* Заглушка «тариф исчерпан» стояла здесь вместо приглашения завести
            бренд — и съедала единственную кнопку, ведущую в онбординг. У свежей
            организации `scan_allowed` равен `false` всегда, поэтому первый экран
            первого клиента состоял из отказа и ссылки на оплату. Бренд заводится
            без тарифа; тариф нужен замеру, и говорится он полосой. */}
        {quotas && !quotas.scan_allowed ? (
          <div className="notice warn" role="status">
            {/* «Тарифа нет» — это `plan_code`. `scan_allowed` снимается ещё и
                приостановкой, и перерасходом, и обещать действующему клиенту
                «вопросы появятся вместе с тарифом» в этих случаях неверно:
                вопросы у него заводятся прямо сейчас. */}
            <b>
              {quotas.plan_code ? 'Замеры остановлены' : 'Замеры начнутся после оплаты'}
            </b>
            {quotas.scan_blocked_reason ?? scanBlockedFallback()}{' '}
            {quotas.plan_code
              ? 'Бренд заводится как обычно — остановлены только замеры.'
              : 'Бренд заводится и до оплаты. Вопросы к нему появятся вместе с тарифом — он определяет, сколько их отслеживать.'}
            {access.reaches('POST', '/billing/subscription/plan') ? (
              <span className="notice-act">
                <a className="btn btn-ghost btn-sm" href="/plan">
                  Открыть тариф
                </a>
              </span>
            ) : null}
          </div>
        ) : null}
        <NeverMeasured
          action={
            access.can('brands.write') ? (
              <a className="btn btn-primary" href="/brands/new">
                Завести бренд
              </a>
            ) : undefined
          }
        />
      </>
    );
  }

  // Бренды есть, но ни один не выбран.
  if (!brandId) {
    return (
      <>
        {head}
        <Empty eyebrow="Выберите бренд" title="Какой бренд смотрим">
          Здесь будет индекс бренда, разбор по движкам и что с этим делать. {pickBrand()}
        </Empty>
      </>
    );
  }

  // Смена бренда — это другой экран, а не обновление этого: пересоздаём его
  // целиком, чтобы числа прошлого бренда не пережили переключение ни на кадр.
  return <BrandScreen key={brandId} brandId={brandId} head={head} quotas={quotas} />;
}

function BrandScreen({
  brandId,
  head,
  quotas,
}: {
  brandId: string;
  head: ReactNode;
  quotas: OrganizationQuotas | null;
}) {
  const access = useAccess();
  const scope = useScope();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  // Выгрузка: какой формат сейчас собирается и что сказать, если не собрался.
  const [preparing, setPreparing] = useState<ReportBuildFormat | null>(null);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const [exportFailure, setExportFailure] = useState<unknown>(null);
  const exporting = useRef<AbortController | null>(null);
  /**
   * Что уже заказано этой кнопкой.
   *
   * Произвольный период сервер НЕ считает повторным заказом того же отчёта
   * (`REGULAR_KINDS` — только недельный и месячный), поэтому второе нажатие
   * после долгой сборки завело бы вторую сборку того же файла. Помним
   * заказанное и отдаём его же.
   */
  const ordered = useRef<Report[]>([]);
  // Ожидание не переживает экран: иначе файл скачается посреди другого раздела.
  useEffect(() => () => exporting.current?.abort(), []);

  const engines = useRead<EngineWithHealth[]>('/engines');
  const cloud = useRead<AttributeCloud>(`/brands/${brandId}/metrics/attributes`);
  const answers = useAnswers(brandId);
  // Окно динамики привязано к ПОКАЗАННОЙ точке, а не к «сегодня», — и к ней же
  // привязана выгрузка. Считай мы окно от текущей даты, замер старше месяца не
  // попал бы на собственный график вовсе, а файл и экран говорили бы о разных
  // периодах. Пока точка неизвестна, ряды не запрашиваются: `useRead` с пустым
  // адресом ничего не грузит.
  const anchor = load.kind === 'ready' ? load.overview.snapshot_date : null;
  const range = anchor
    ? { from: backFrom(anchor, WINDOW_DAYS - 1), to: anchor, basis: 'weekly' as const }
    : undefined;
  const daily = useRead<DailySnapshot[]>(
    anchor ? `/brands/${brandId}/metrics/daily` : null,
    range,
  );
  const rivalRows = useRead<CompetitorSnapshot[]>(
    anchor ? `/brands/${brandId}/metrics/competitors` : null,
    range,
  );

  useEffect(() => {
    const abort = new AbortController();
    let timer: number | undefined;
    // Сколько попыток подряд не дошло. Считается здесь, а не в состоянии: от
    // него ничего не рисуется, а перерисовка на каждую неудачу перезапускала бы
    // эффект.
    let misses = 0;

    const tick = () => {
      requestWith<BrandOverview>(`/brands/${brandId}/overview`, { signal: abort.signal })
        .then(({ body, retryAfter }) => {
          misses = 0;
          setLoad({ kind: 'ready', overview: body });
          // Опрашиваем, только пока замер действительно идёт. Терминальный
          // ответ приходит без Retry-After — это и есть команда остановиться.
          const running =
            body.active_run &&
            (body.active_run.state === 'queued' || body.active_run.state === 'running');
          if (running) {
            timer = window.setTimeout(tick, (retryAfter ?? POLL_FALLBACK) * 1000);
          }
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;

          // Первая загрузка и очередной опрос — разные события, и путать их
          // нельзя. Пока идёт замер, опрос ходит каждые три секунды; одна не
          // дошедшая попытка (лифт, метро, перезапуск пода) стирала **весь**
          // экран с числами и заменяла его красной плашкой — при том, что
          // продукт в этом же случае сам пишет человеку «данные на экране
          // остались прежними». Числа были, их затирали, и обратно они не
          // возвращались: `tick` после ошибки не перепланировался, помогала
          // только перезагрузка страницы.
          misses += 1;
          setLoad((current) => {
            // Чисел ещё не было — это настоящая неудача загрузки.
            if (current.kind !== 'ready') return { kind: 'failed', error };
            // Числа есть: оставляем их и помечаем, когда они сняты.
            return current.stale ? current : { ...current, stale: { since: Date.now() } };
          });

          // Пробуем снова, реже: 3 → 6 → 12 секунд. Опрос, который умер от
          // одного неудачного тика, — это карточка, вечно показывающая
          // «Собираем», и человек, уверенный, что продукт завис.
          timer = window.setTimeout(
            tick,
            Math.min(POLL_FALLBACK * 2 ** misses, POLL_MAX) * 1000,
          );
        });
    };

    tick();
    return () => {
      abort.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [brandId]);

  if (load.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }

  if (load.kind === 'failed') {
    // Квота — это не поломка экрана, а состояние тарифа, и говорится оно иначе.
    const problem = load.error instanceof ApiError ? load.error.problem : null;
    if (problem?.code === 'quota_exceeded' || problem?.code === 'scan_blocked') {
      return (
        <>
          {head}
          <QuotaExhausted
            quotas={quotas}
            canPay={access.reaches('POST', '/billing/subscription/plan')}
            action={
              <a className="btn btn-primary" href="/plan">
                Открыть тариф
              </a>
            }
          />
        </>
      );
    }
    return (
      <>
        {head}
        <ErrorNotice error={load.error} />
      </>
    );
  }

  const { overview } = load;
  const run = overview.active_run;
  // Заказывать сборку вправе не всякая роль — значит, и кнопки выгрузки есть не
  // у всякой: серой кнопки с подсказкой в этой системе не бывает.
  const canExport = access.reaches('POST', '/reports');
  const measuring = run && (run.state === 'queued' || run.state === 'running');
  const partial = run && (run.state === 'partial' || (run.failed_tasks ?? 0) > 0);

  // Замера ещё не было: сетки нет и прогона нет.
  if (!overview.index && !run) {
    return (
      <>
        {head}
        <NeverMeasured
          brandName={scope.brand?.name}
          action={
            // Ссылка на «Прогоны», а не кнопка. Кнопка здесь стояла без
            // обработчика вовсе: новый клиент видел на первом экране
            // единственное синее действие, жал — и не получал ни перехода, ни
            // ошибки. Настоящий запуск живёт на «Прогонах» вместе с выбором
            // движков, ключом идемпотентности и разбором `quota_exceeded` —
            // разводить второй запуск по экранам значит заводить второе место,
            // где всё это придётся повторить.
            access.can('runs.start') ? (
              <Link className="btn btn-primary" to="/runs">
                Запустить первый замер
              </Link>
            ) : undefined
          }
        />
        {/* Частота видна и до первого замера: ритм выбирают, когда бренд
            только завели, а не после того, как заплатили за месяц лишних
            проходов. */}
        <ScanCadencePicker brandId={brandId} />
      </>
    );
  }

  const catalogue = engines.kind === 'ready' ? engines.data : [];
  const titles = engineTitles(catalogue);
  const order = byEngineOrder(catalogue);
  const rows = overview.engines ?? [];

  // Строим от справочника, а не от снимка: движок, который не ответил, должен
  // быть виден строкой, а не исчезнуть из среднего.
  const active = catalogue.filter((engine) => engine.is_active);
  const codes = (
    active.length
      ? active.map((engine) => engine.code)
      : rows.map((row) => row.engine_code)
  ).sort(order);

  const byCode = new Map(rows.map((row) => [row.engine_code, row]));

  const engineBars: BarItem[] = codes.map((code) => {
    const row = byCode.get(code);
    const answered = row && (row.organic_answers ?? 0) > 0;
    return {
      key: code,
      name: engineTitle(titles, code),
      note: answered
        ? `${coverageOf(row.organic_mentions ?? 0, row.organic_answers ?? 0, 'ответ', 'ответа', 'ответов')}`
        : 'движок не ответил в этом прогоне',
      value: answered ? (row.visibility ?? null) : null,
    };
  });

  // Своя строка среди конкурентов: акцент достаётся лидеру, даже если лидер —
  // конкурент. Себя находят чернильным ребром и меткой «Вы».
  const rivals = overview.top_competitors ?? [];
  const rivalBars: BarItem[] = [
    {
      key: 'self',
      name: scope.brand?.name ?? 'Ваш бренд',
      value: overview.visibility ?? null,
      isOwn: true,
    },
    ...rivals.map((rival) => ({
      key: rival.competitor_id,
      name: rival.competitor_name,
      // Вторая строка колонки имени в лидерборде — это **категория бренда**
      // (components.md). Голое название движка стояло там без подписи и
      // читалось как часть названия компании: «ТрансАвто / Нейро». Хуже того,
      // само число сведено по всем ответившим движкам, а под ним молча стоял
      // один — основание противоречило подписи, а основание в этом продукте
      // и есть причина верить числу.
      note: `чаще всего называет: ${engineTitle(titles, rival.engine_code)}`,
      value: rival.visibility ?? null,
    })),
  ];

  // Экран НЕ СЧИТАЕТ НИЧЕГО. Все числа точки приходят посчитанными: их считает
  // сводка сканера единственной реализацией формулы, и публичный отчёт читает
  // ту же строку. Пока арифметика жила здесь, она брала одним весом —
  // органическими ответами — все метрики подряд: для видимости это верно, её
  // знаменатель и есть ответы, а для «в топ-3» знаменатель другой, и на экране
  // стояло число, не равное ни одной честной доле.
  const totals = overview.totals ?? null;
  const top3 = totals?.top3_share ?? null;
  const avgPosition = totals?.avg_position ?? null;
  const positioned = totals?.positioned ?? null;
  const mentions = totals?.organic_mentions ?? null;
  // Тональность берётся ОТТУДА ЖЕ, откуда остальные числа точки. Здесь она
  // складывалась на клиенте — облако признаков по движкам, взвешенное по
  // обсуждавшим ответам, — и это было второе выражение метрики: сводка сканера
  // считает тон по упоминаниям с определённой тональностью, и два способа
  // сходятся только там, где у каждого движка тон посчитан. Показывать рядом с
  // одним числом дельту, снятую с другого, нельзя тем более.
  const tone = totals?.sentiment_score ?? null;
  // Изменение к предыдущей точке — тоже посчитанное сервером, по тем же
  // округлённым числам, что напечатаны рядом. `null` — первый замер бренда.
  const delta = overview.totals_delta ?? null;

  // Динамика на обзоре — та же линия, что на «Динамике»: один способ собрать
  // ряд на весь кабинет (`ui/series.ts`), иначе два экрана нарисовали бы по
  // одним данным два разных графика.
  const own = daily.kind === 'ready' ? brandSeries(daily.data) : [];
  const rivalLines = rivalRows.kind === 'ready' ? rivalSeriesOf(rivalRows.data) : [];
  const trend: Series[] = [
    { key: 'own', name: 'Вы', points: own, subject: true },
    ...rivalLines.slice(0, RIVAL_LINES),
  ];

  /**
   * Выгрузка того, что на экране.
   *
   * Период привязан к ПОКАЗАННОЙ точке, а не к «сегодня»: файл обязан
   * содержать ровно тот замер, числа которого человек видит, иначе «скачать»
   * означало бы «получить похожий документ за соседний период».
   */
  const download = (format: ReportBuildFormat) => {
    const controller = new AbortController();
    exporting.current?.abort();
    exporting.current = controller;
    setPreparing(format);
    setExportNote(null);
    setExportFailure(null);
    orderAndDownload(
      {
        kind: 'custom',
        format,
        brandId,
        periodStart: backFrom(overview.snapshot_date, WINDOW_DAYS - 1),
        periodEnd: overview.snapshot_date,
        siblings: ordered.current,
      },
      controller.signal,
      (created) => ordered.current.push(created),
    )
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof ExportFailure) setExportNote(error.message);
        else setExportFailure(error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreparing(null);
      });
  };

  return (
    <>
      <div className="screen-head">
        <div>
          <h1>{scope.brand ? scope.brand.name : 'Обзор бренда'}</h1>
          {/* Основание экрана стоит ОДИН раз и в шапке. Прежде оно висело
              подписью к каждому блоку — у индекса, под сводкой, у графика, у
              таблицы, — и повторялось на экране слово в слово четыре раза:
              стена серого текста, которую перестают читать целиком, включая то
              место, где она нужна. Правило и есть экранное, а не поштучное
              (data-viz.md §1): что измерено, по скольким движкам, за какой
              период и насколько полно — один раз, рядом с именем бренда.

              Один раз — не значит «одна дата»: полнота сетки и счёт ответивших
              движков остаются здесь же. Без них закрытая неполная точка выглядит
              полной, потому что предупреждение о сорванном замере живёт только у
              активного прогона. */}
          <p className="screen-basis">
            {snapshotBasis({
              date: overview.snapshot_date,
              rows,
              codes,
              gridComplete: overview.grid_complete,
            })}
          </p>
        </div>
        <div className="head-tools">
          {/* Выгрузка стоит слева от запуска: сначала то, что делают с уже
              измеренным, потом то, что заводит новое измерение. */}
          {canExport
            ? EXPORTS.map((format) => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  type="button"
                  onClick={() => download(format)}
                  disabled={preparing !== null}
                  aria-busy={preparing === format}
                >
                  {preparing === format
                    ? `Готовим ${FORMAT[format]}…`
                    : `Скачать ${FORMAT[format]}`}
                </button>
              ))
            : null}
          {access.can('runs.start') && !measuring ? (
            <Link className="btn btn-primary btn-sm" to="/runs">
              Запустить замер
            </Link>
          ) : null}
        </div>
      </div>

      {exportFailure ? <ErrorNotice error={exportFailure} /> : null}
      {exportNote ? (
        <p className="notice warn" role="status">
          <b>Файл пока не собрался</b>
          {exportNote}
        </p>
      ) : null}

      {/* Связи нет, но числа настоящие — просто снятые раньше. Полоса
          неблокирующая и стоит над числами, а не вместо них: экран, стёртый
          из-за одной не дошедшей попытки опроса, — это потеря данных на
          ровном месте. */}
      {load.stale ? (
        <div className="notice warn" role="status">
          <b>Не смогли обновить</b>
          Показываем данные на {timeOfDay(new Date(load.stale.since))}. Продолжаем
          пробовать — как только связь вернётся, числа обновятся сами.
        </div>
      ) : null}

      {/* Идущий замер стоит над числами: человек должен понимать, что цифры
          сейчас изменятся. */}
      {measuring ? <Measuring run={run} /> : null}

      {/* Неполнота раскрывается на том же экране, а не в журнале. */}
      {/* Каталог сюда передаётся не для красоты: без него сорвавшийся движок
          назывался бы машинным кодом («deepseek») прямо в блоке про сбой, а
          строкой ниже, в разборе, — своим именем («DeepSeek»). */}
      {!measuring && partial ? (
        <PartialRun run={run} engineTitle={(code) => engineTitle(titles, code)} />
      ) : null}

      {/* Частота стоит под состоянием замера и над числами: сорванный замер и
          идущий прогон объясняют сегодняшние цифры, а частота — когда появится
          следующая точка. Настройка ниже новости, но выше самих чисел. */}
      <ScanCadencePicker brandId={brandId} />

      <section className="summary">
        <IndexGauge
          value={overview.index?.score}
          deltaValue={overview.index_delta}
          // Одна формулировка дельты индекса на весь продукт: голое число без
          // единицы. «К прошлой точке» — это основание, и живёт оно в подписи
          // под показателем, а не приставкой к числу: иначе одна и та же
          // величина называется на «Обзоре» и на «Динамике» двумя способами.
          deltaText={
            overview.index_delta === null || overview.index_delta === undefined
              ? undefined
              : indexDelta(overview.index_delta)
          }
        />
        {/* У самого прибора остаётся только то, что относится к нему одному:
            неполная точка — это предупреждение о числе, а не основание экрана. */}
        {overview.index?.is_complete === false ? (
          <Stamp>Точка неполная — балл ещё сдвинется</Stamp>
        ) : null}
      </section>

      {/* Движение стоит ВПЛОТНУЮ к числу, на той же строке: «42,0% +0,1 п. п.».
          Отдельной строкой под числом оно читалось как ещё одно показание, и
          клиент спрашивал, какое из двух чисел его. Перенос строки здесь —
          не вёрстка, а смысл. */}
      <dl className="facts">
        <div className="fact">
          <dt>Видимость</dt>
          <dd className="tnum">
            {overview.visibility === null || overview.visibility === undefined
              ? NOT_MEASURED
              : percent(overview.visibility, 1)}
            <FactDelta
              value={overview.visibility_delta_pp}
              // Тот же знак после запятой, что у самого числа. С округлением до
              // целого подпись говорила «+0 п. п.» там, где на экране 41,9%
              // сменялось на 42,0%.
              text={(value) => deltaPoints(value, 1)}
            />
          </dd>
          <span className="stamp fact-note">Доля ответов, в которых назван бренд</span>
        </div>

        <div className="fact">
          <dt>В топ-3</dt>
          <dd className="tnum">
            {top3 === null ? NOT_MEASURED : percent(top3, 1)}
            <FactDelta
              value={delta?.top3_share_pp}
              text={(value) => deltaPoints(value, 1)}
            />
          </dd>
          {/* Подпись называет знаменатель словами человека: считается доля не от
              всех запросов, а от тех ответов, где бренд вообще назвали. Сколько
              таких упоминаний и у скольких из них разобрано место — соседняя
              плитка, чтобы здесь стояло одно утверждение, а не три. */}
          <span className="stamp fact-note">
            Когда вас упоминали, то как часто вы были в топ-3
          </span>
        </div>

        <div className="fact">
          <dt>Средняя позиция</dt>
          <dd className="tnum">
            {avgPosition === null ? NOT_MEASURED : position(avgPosition)}
            {/* Единственный показатель сводки, у которого уменьшение — это
                улучшение. Раскрасить его как падение значит сказать клиенту
                обратное тому, что произошло. */}
            <FactDelta
              value={delta?.avg_position}
              text={deltaPosition}
              goodWhenNegative
            />
          </dd>
          {/* Отсутствие числа и ноль — разные утверждения, и подпись обязана их
              различать: с одной строкой экран с непосчитанной сводкой заявлял бы
              «разобранных мест не было», то есть выдавал наше незнание за факт
              про клиента. */}
          <span className="stamp fact-note">
            {avgPosition === null
              ? 'Позицию не с чего считать: разобранных мест не было'
              : 'Меньше — лучше'}
          </span>
        </div>

        <div className="fact">
          <dt>Тональность</dt>
          <dd className="tnum">
            {tone === null ? NOT_MEASURED : sentimentFmt(tone)}
            {/* Единица у движения та же, что у самого числа: тональность живёт в
                пунктах шкалы −50…+50, а не в процентных пунктах. */}
            <FactDelta value={delta?.sentiment_score} text={sentimentDelta} />
          </dd>
          <span className="stamp fact-note">
            {tone === null
              ? 'Бренд ни в одном ответе не обсуждали — только перечисляли'
              : 'Шкала от −50 до +50 по ответам, где бренд обсуждают, а не перечисляют'}
          </span>
        </div>
      </dl>

      {/* Чем измерение сравнивается — правило экранное, а не поштучное: основание
          стоит один раз под всеми показателями (data-viz.md §1). Дата названа
          словами намеренно: замер бывает пропущен, и «динамика» без даты — это
          стрелка неизвестно куда.

          Здесь же качество нашего разбора. Отдельной плиткой «Место разобрано»
          оно стояло рядом с показателями бренда и читалось как ещё одно
          показание про него, хотя говорит о нас. Убрать его совсем было нельзя:
          «в топ-3» считается ТОЛЬКО по упоминаниям с разобранным местом, и
          Perplexity, назвавший бренд шесть раз при одном разобранном месте, даёт
          честные 100% по одному упоминанию. Число, у которого не видно
          основания, — это то самое «два разных балла», от которого продукт
          отказывается. */}
      {/* Под плитками стоит РОВНО то, чего нет в клейме над ними: с чем сравнены
          дельты и на скольких упоминаниях держатся доли по позиции. Период,
          движки и полнота сетки названы клеймом индекса выше — правило основания
          экранное, а не поштучное (data-viz.md §1), и повторённое второй раз оно
          превращается в стену серого текста, которую перестают читать.

          Здесь же стояли уверенность и волатильность. Уверенность печаталась
          строкой «Уверенность высокая — столько согласия между двумя прогонами
          одной клетки»: числа, к которому относилось «столько», в строке не было
          вовсе — доля согласия ушла в подсказку, а слово осталось. Волатильность
          живёт на «Динамике», где есть сам ряд: разброс без ряда нечем прочесть. */}
      <Stamp>
        {delta === null
          ? 'Сравнивать не с чем: это первый замер бренда'
          : `Изменения — к замеру ${dateProse(delta.previous_snapshot_date)}`}
        {/* Условие стоит на САМИХ счётчиках, а не на доле. С проверкой доли и
            `?? 0` строка на неполной точке печатала «место разобрано у 0 из 0»,
            то есть выдавала отсутствие данных за факт нуля. */}
        {positioned === null || mentions === null || mentions === 0
          ? ''
          : ` · место разобрано у ${int(positioned)} из ${countOf(mentions, 'упоминания', 'упоминаний', 'упоминаний')}`}
      </Stamp>

      {/* Живой ответ движка стоит сразу за сводкой и перед разрезами: доли и
          позиции — это пересказ, а здесь показано само высказывание, из
          которого они посчитаны. Дальше по экрану он оказался бы под тремя
          графиками, то есть там, где до него не доходят. Блок общий с разрезом
          по движкам (`ui/LiveAnswer.tsx`) и на пустых ответах не рисуется
          вовсе. */}
      <LiveAnswer brandId={brandId} answers={answers} titles={titles} />

      <QuestionStates brandId={brandId} />

      <div className="cols block">
        <section>
          <h2>Где нас называют</h2>
          {/* Полосы стоят без подписи: как их читать, сказано врезом над ними, а
              период, движки и полнота сетки — основанием экрана в шапке. Здесь
              подпись повторяла и то и другое слово в слово. */}
          <Bars items={engineBars} />
        </section>

        <section>
          <h2>Кого называют вместо нас</h2>
          {rivals.length === 0 ? (
            /* Раздел был назван словами, а ссылки не было: экран сообщал, чего
               не хватает, и оставлял человека искать это в меню самому. */
            <p className="stamp">
              Конкуренты не заведены. Пока их нет, сравнивать не с кем —{' '}
              <Link to="/competitors">добавьте их</Link>.
            </p>
          ) : (
            <Bars items={rivalBars} />
          )}
        </section>
      </div>

      {/* Диагноз стоит сразу за разрезом по движкам, потому что говорит о тех же
          движках и о том же числе: полосы выше отвечают «сколько», а этот блок —
          «почему столько». Дальше по экрану он оказался бы за графиками, то есть
          там, где до него не доходят.

          Разрез по вопросам печатает текст вопроса, то есть семантическое ядро, и
          клиенту агентства он закрыт — кнопки у него нет вовсе, а не серая. */}
      <MemoryBlock
        brandId={brandId}
        memory={overview.memory}
        catalogue={catalogue}
        titles={titles}
        order={order}
        canOpenQuestions={access.reaches('GET', MEMORY_QUESTIONS_PATH)}
      />

      <section className="block">
        <h2>Как это менялось</h2>
        {daily.kind === 'loading' ? (
          <Skeleton rows={2} />
        ) : daily.kind === 'failed' ? (
          // Не дошедший запрос — это отказ, а не «данных пока нет». Пустой
          // график со словами «ряд появится после второго замера» выдавал бы
          // нашу поломку за состояние бренда.
          <ErrorNotice error={daily.error} />
        ) : (
          <Trend
            series={trend}
            emptyText="Ряд появится после второго замера: одна точка ещё не линия."
          />
        )}
      </section>

      {overview.benchmark ? (
        <section className="block">
          <h2>Как это выглядит на фоне похожих брендов</h2>
          <div className="panel">
            <p>
              Медиана по похожим брендам —{' '}
              <b className="tnum">{percent(overview.benchmark.visibility_p50 ?? 0, 1)}</b>
              , верхняя четверть начинается с{' '}
              <b className="tnum">{percent(overview.benchmark.visibility_p75 ?? 0, 1)}</b>
              .
            </p>
            <p className="stamp">
              По{' '}
              {countOf(overview.benchmark.brands_counted, 'бренду', 'брендам', 'брендам')}{' '}
              похожего бизнеса, без имён
            </p>
          </div>
        </section>
      ) : null}

      {cloud.kind === 'ready' && cloud.data.items.length > 0 ? (
        <section className="block">
          <h2>О чём говорят, когда говорят о нас</h2>
          <div className="tbl-wrap">
            <table className="mini">
              {/* Подпись называет таблицу — и только. Период, движки и полнота
                  сетки стоят клеймом под индексом, а здесь повторялись слово в
                  слово четвёртый раз на экране: основание, повторённое всюду,
                  перестают читать везде, включая то место, где оно нужно. */}
              <caption>Свойства и оценки</caption>
              <thead>
                <tr>
                  <th scope="col">Свойство</th>
                  <th scope="col" className="num">
                    Хорошо
                  </th>
                  <th scope="col" className="num">
                    Нейтрально
                  </th>
                  <th scope="col" className="num">
                    Плохо
                  </th>
                </tr>
              </thead>
              <tbody>
                {cloud.data.items.slice(0, 8).map((item) => (
                  <tr key={item.label}>
                    <td>{item.label}</td>
                    {/* Цвет здесь ДУБЛИРУЕТ заголовок колонки, а не заменяет
                        его: «хорошо» и «плохо» написаны словами наверху, и
                        человек, не различающий эти два цвета, читает таблицу так
                        же. Ноль не красится вовсе — красный ноль сообщал бы о
                        плохом там, где плохого не нашли. */}
                    <td className={`tnum num${item.positive > 0 ? ' good' : ''}`}>
                      {item.positive}
                    </td>
                    <td className="tnum num">{item.neutral}</td>
                    <td className={`tnum num${item.negative > 0 ? ' bad' : ''}`}>
                      {item.negative}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
}

/**
 * Движение показателя рядом с самим числом. Пусто, когда сравнивать не с чем:
 * ноль здесь означал бы «не изменилось», а это утверждение о бренде, которого
 * мы не измеряли. Что сравнивается с чем, сказано один раз под всей сводкой.
 */
/**
 * По каким вопросам нас называют, а по каким нет.
 *
 * Претензия клиента звучит как «в кабинете этого не посмотреть», поэтому ответ
 * стоит здесь, а не тринадцатым пунктом меню. Каждое число — вход в список с
 * уже выставленным отбором: цифра без перехода оставляет человека искать те же
 * вопросы руками.
 *
 * Числа считает сервер по ВСЕМУ замеру, а не по загруженной странице: иначе они
 * разошлись бы со списком, в который ведут.
 */
function QuestionStates({ brandId }: { brandId: string }) {
  // Строки здесь не нужны, нужна сводка: страница просится самой короткой.
  const page = useRead<QuestionVisibilityPage>(
    `/brands/${brandId}/questions/visibility`,
    { limit: 1 },
  );

  // Не дошедший запрос — это отказ, а не «вопросов нет»: пропавший блок
  // неотличим от продукта, в котором такого экрана не бывает.
  if (page.kind === 'failed') {
    return (
      <section className="block">
        <h2>По каким вопросам нас называют</h2>
        <ErrorNotice error={page.error} />
      </section>
    );
  }
  if (page.kind === 'loading') return <Skeleton rows={1} />;
  if (!page.data.run_id || page.data.summary.questions_total === 0) return null;

  const summary = page.data.summary;
  const counts: { state: string; title: string; value: number }[] = [
    { state: 'unseen', title: 'Не называет никто', value: summary.unseen },
    { state: 'partial', title: 'Называют не все', value: summary.partial },
    { state: 'named', title: 'Называют все движки', value: summary.named },
  ];

  return (
    <section className="block">
      <h2>По каким вопросам нас называют</h2>
      <dl className="facts three">
        {counts.map((count) => (
          <div className="fact" key={count.state}>
            <dt>{count.title}</dt>
            <dd className="tnum">
              <Link to={`/visibility?state=${count.state}`}>{int(count.value)}</Link>
            </dd>
            <span className="stamp fact-note">
              из {int(summary.questions_total)} вопросов замера
            </span>
          </div>
        ))}
      </dl>
      {/* «Не измерено» — не ноль и не результат: это дыра в сетке. Цифрой рядом
          с тремя другими она читалась бы как «сервис не работал». */}
      {summary.unmeasured > 0 ? (
        <Stamp>
          Сетка закрыта не полностью: ответов нет по {int(summary.unmeasured)} вопросам.{' '}
          <Link to="/visibility?state=unmeasured">Посмотреть</Link>
        </Stamp>
      ) : null}
    </section>
  );
}

function FactDelta({
  value,
  text,
  /** У средней позиции уменьшение — улучшение, поэтому направление задаётся снаружи. */
  goodWhenNegative = false,
}: {
  value: number | null | undefined;
  text: (value: number) => string;
  goodWhenNegative?: boolean;
}) {
  if (value === null || value === undefined) return null;
  return <Pill value={value} text={text(value)} goodWhenNegative={goodWhenNegative} />;
}
